import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { collapseWhitespace } from "../../boundaries/label.formatter";
import { pageDisplayLabel } from "../pages/boundaries/page-label.parser";

export function connectionLabels(
  pages: OverviewResponse["pages"],
  site: OverviewResponse["site"],
): string {
  const labels = pages.slice(0, 3).map((page) => pageDisplayLabel(page, site));
  const remainder = pages.length - labels.length;
  return `: ${labels.join(", ")}${remainder > 0 ? ` and ${remainder} more` : ""}`;
}

export function wrapEstateLabel(label: string, limit: number): string[] {
  const characters = Array.from(collapseWhitespace(label));
  if (displayWidth(characters) <= limit) return [characters.join("")];

  const cut = lineBreakIndex(characters, limit);
  const first = characters.slice(0, cut).join("").trim();
  const second = truncateToWidth(characters.slice(cut).join("").trim(), limit);
  return [first, second].filter(Boolean);
}

function lineBreakIndex(value: string[], limit: number): number {
  let width = 0;
  let lastSpace = -1;
  for (let index = 0; index < value.length; index += 1) {
    const nextWidth = width + characterWidth(value[index]);
    if (nextWidth > limit) {
      return lastSpace > 0 ? lastSpace + 1 : Math.max(1, index);
    }
    width = nextWidth;
    if (value[index] === " ") lastSpace = index;
  }
  return value.length;
}

function truncateToWidth(value: string, limit: number): string {
  const characters = Array.from(value);
  if (displayWidth(characters) <= limit) return value;
  const suffix = "...";
  const cut = lineBreakIndex(characters, limit - suffix.length);
  return `${characters.slice(0, cut).join("").trimEnd()}${suffix}`;
}

function displayWidth(value: string[]): number {
  return value.reduce(
    (total, character) => total + characterWidth(character),
    0,
  );
}

function characterWidth(value: string | undefined): number {
  return value && (value.codePointAt(0) ?? 0) > 0x7f ? 2 : 1;
}
